import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FiUsers, FiHome, FiCalendar, FiDollarSign, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useUser } from '../../contexts/UserContext';
import { useDashboardFilter } from '../../contexts/DashboardFilterContext';
import { dashboardService } from '../../services/dashboard.service';
import type {
  DashboardOverviewResponse,
  RevenueTrendResponse,
  AppointmentStatusDistributionResponse,
  ServiceAppointmentComparisonResponse,
} from '../../types/dashboard.types';
import MetricCard from '../../components/Dashboard/MetricCard/MetricCard';
import RevenueTrendChart from '../../components/Dashboard/Charts/RevenueTrendChart';
import AppointmentStatusChart from '../../components/Dashboard/Charts/AppointmentStatusChart';
import ServiceComparisonChart from '../../components/Dashboard/Charts/ServiceComparisonChart';
import FilterPanel from '../../components/Dashboard/FilterPanel/FilterPanel';
import styles from './Dashboard.module.css';

const DashboardSuperAdmin: React.FC = () => {
  const { user } = useUser();
  const { dateRange, customRange, onFilterChange, filterLoading, setFilterLoading } = useDashboardFilter();
  const [loading, setLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);
  const [overview, setOverview] = useState<DashboardOverviewResponse | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueTrendResponse['data']>([]);
  const [revenueError, setRevenueError] = useState(false);
  const [revenueIncomplete, setRevenueIncomplete] = useState(false);
  const [appointmentData, setAppointmentData] = useState<AppointmentStatusDistributionResponse | null>(null);
  const [appointmentError, setAppointmentError] = useState(false);
  const [appointmentIncomplete, setAppointmentIncomplete] = useState(false);
  const [serviceData, setServiceData] = useState<ServiceAppointmentComparisonResponse['data']>([]);
  const [serviceCategoryNames, setServiceCategoryNames] = useState<string[]>([]);
  const [serviceError, setServiceError] = useState(false);
  const [serviceIncomplete, setServiceIncomplete] = useState(false);
  const isInitialMount = useRef(true);

  const fetchAll = useCallback(async () => {
    const custom = dateRange === 'custom' ? customRange ?? undefined : undefined;
    const [overviewRes, revenueRes, appointmentRes, serviceRes] = await Promise.allSettled([
      dashboardService.getDashboardOverview(dateRange, custom ?? undefined),
      dashboardService.getRevenueTrend(dateRange, custom ?? undefined),
      dashboardService.getAppointmentStatusDistribution(dateRange, custom ?? undefined),
      dashboardService.getServiceAppointmentComparison(dateRange, custom ?? undefined),
    ]);

    const o = overviewRes.status === 'fulfilled' ? overviewRes.value : null;
    const r = revenueRes.status === 'fulfilled' ? revenueRes.value : null;
    const a = appointmentRes.status === 'fulfilled' ? appointmentRes.value : null;
    const s = serviceRes.status === 'fulfilled' ? serviceRes.value : null;

    setOverviewError(overviewRes.status === 'rejected');
    setOverview(o ?? null);
    setRevenueError(revenueRes.status === 'rejected');
    setRevenueIncomplete(!r?.data?.length);
    setRevenueData(r?.data ?? []);
    setAppointmentError(appointmentRes.status === 'rejected');
    setAppointmentIncomplete(!a);
    setAppointmentData(a ?? null);
    setServiceError(serviceRes.status === 'rejected');
    setServiceIncomplete(!s?.data?.length);
    setServiceData(s?.data ?? []);
    setServiceCategoryNames(s?.categoryNames ?? []);
  }, [dateRange, customRange]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        setLoading(true);
      } else {
        setFilterLoading(true);
      }
      setOverviewError(false);
      setRevenueError(false);
      setAppointmentError(false);
      setServiceError(false);
      try {
        await fetchAll();
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFilterLoading(false);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [fetchAll, setFilterLoading]);

  const name = user?.fullName ?? 'Super Admin';
  const rangeLabel = dateRange === 'today'
    ? 'today'
    : dateRange === 'last7days'
      ? 'this week'
      : dateRange === 'last30days'
        ? 'this month'
        : dateRange === 'alltime'
          ? 'all time'
          : 'in this range';
  const showOverviewError = overviewError && !loading;
  const showOverviewEmpty = !loading && !overviewError && !overview;

  return (
    <div className={styles.container}>
      {filterLoading && <div className={styles.topLoadingBar} />}
      <section className={styles.greeting}>
        <div className={styles.greetingLeft}>
          <h1 className={styles.greetingName}>Hi, {name}</h1>
          <p className={styles.greetingSubtitle}>Here&apos;s what&apos;s happening {rangeLabel}</p>
        </div>
        <div className={styles.greetingRight}>
          <FilterPanel
            value={dateRange}
            customRange={customRange}
            onFilterChange={onFilterChange}
            loading={filterLoading}
          />
        </div>
      </section>

      {loading && (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Loading dashboard...</span>
        </div>
      )}

      {!loading && (
        <>
          {showOverviewError && (
            <div className={styles.errorState}>Error loading data. Please try again.</div>
          )}
          {showOverviewEmpty && !showOverviewError && (
            <div className={styles.noData}>No data available</div>
          )}
          {!showOverviewError && !showOverviewEmpty && overview && (
            <>
              <section className={styles.cardsGrid} aria-label="Overview metrics">
                <MetricCard
                  value={overview.activeCustomers}
                  label="Active Customers"
                  trend={overview.activeCustomersTrend}
                  trendDirection={overview.activeCustomersTrendDirection}
                  trendPositiveMeansUp
                  icon={<FiUsers size={24} />}
                  iconVariant="purple"
                />
                <MetricCard
                  value={overview.activeSalons}
                  label="Active Salons"
                  trend={overview.activeSalonsTrend}
                  trendDirection={overview.activeSalonsTrendDirection}
                  trendPositiveMeansUp
                  icon={<FiHome size={24} />}
                  iconVariant="purple"
                />
                <MetricCard
                  value={overview.totalBookings}
                  label="Total Bookings"
                  trend={overview.totalBookingsTrend}
                  trendDirection={overview.totalBookingsTrendDirection}
                  trendPositiveMeansUp
                  icon={<FiCalendar size={24} />}
                  iconVariant="purple"
                />
                <MetricCard
                  value={overview.revenueGmv}
                  label="Revenue (AED)"
                  trend={overview.revenueGmvTrend}
                  trendDirection={overview.revenueGmvTrendDirection}
                  trendPositiveMeansUp
                  icon={<FiDollarSign size={24} />}
                  iconVariant="pink"
                />
                <MetricCard
                  value={overview.completionRate}
                  label="Completion Rate (%)"
                  trend={overview.completionRateTrend}
                  trendDirection={overview.completionRateTrendDirection}
                  trendPositiveMeansUp
                  icon={<FiCheckCircle size={24} />}
                  iconVariant="pink"
                />
                <MetricCard
                  value={overview.cancellationRate}
                  label="Cancellation Rate (%)"
                  trend={overview.cancellationRateTrend}
                  trendDirection={overview.cancellationRateTrendDirection}
                  trendPositiveMeansUp={false}
                  icon={<FiXCircle size={24} />}
                  iconVariant="pink"
                />
              </section>
              <section className={styles.chartsGrid} aria-label="Charts">
                <RevenueTrendChart data={revenueData} loading={false} error={revenueError} incomplete={revenueIncomplete} title="Appointments & Amount" />
                <div className={styles.chartsRow}>
                  <ServiceComparisonChart data={serviceData} categoryNames={serviceCategoryNames} loading={false} error={serviceError} incomplete={serviceIncomplete} title="Service Appointment Comparison" />
                  <AppointmentStatusChart data={appointmentData} loading={false} error={appointmentError} incomplete={appointmentIncomplete} title="Appointment statistics" />
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardSuperAdmin;
